/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { AsyncPipe } from "@angular/common";
import { Component, Inject } from "@angular/core";
import { FormBuilder, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef, MatDialogTitle } from "@angular/material/dialog";
import { BehaviorSubject, Observable, Subject, first } from "rxjs";
import {
    ButtonComponent, FormInputComponent, FormPasswordInputComponent, ModalCloseButtonComponent,
    ScrollContainerComponent, requiredValidator
} from "typedb-cloud-framework";
import { Cluster } from "../../../../concept/cluster";
import { ClusterApi } from "../../../../service/cluster/cluster-api.service";
import { DialogResult } from "../../../../service/dialog.service";
import { SnackbarService } from "../../../../service/snackbar.service";
import { cryptoRandomAlphanumeric } from "../../../../util";
import { buildConnectionString, clusterCredentialFilename, parseVersionString } from "../shared";
import { ReauthenticateComponent, ReauthStage } from "../../../../framework/reauthenticate/reauthenticate.component";

export type ClusterResetUserPasswordDialogData = { cluster: Cluster, serverAddresses$: Observable<string[]> };

@Component({
    selector: "tp-cluster-reset-user-password-dialog",
    templateUrl: "./cluster-reset-user-password-dialog.component.html",
    styleUrls: ["./cluster-reset-user-password-dialog.component.scss"],
    imports: [
        AsyncPipe, MatDialogContent, MatDialogTitle, ModalCloseButtonComponent, ScrollContainerComponent,
        ButtonComponent, FormInputComponent, FormPasswordInputComponent, FormsModule, ReactiveFormsModule,
        ReauthenticateComponent,
    ],
})
export class ClusterResetUserPasswordDialogComponent {
    readonly isSubmitting$ = new Subject<boolean>();
    reauthRequired = true;
    readonly dialogTitle$ = new BehaviorSubject("Reauthenticate to reset user password");
    credentialsDownloading = false;
    readonly form = this.formBuilder.nonNullable.group({
        username: ["", [requiredValidator]],
        newPassword: ["", [requiredValidator]],
    });

    constructor(
        private dialogRef: MatDialogRef<ClusterResetUserPasswordDialogComponent, DialogResult>,
        private formBuilder: FormBuilder, @Inject(MAT_DIALOG_DATA) public data: ClusterResetUserPasswordDialogData,
        private clusterApi: ClusterApi, private snackbar: SnackbarService,
    ) {
        this.generatePassword();
    }

    generatePassword() {
        this.form.patchValue({ newPassword: cryptoRandomAlphanumeric(16) });
    }

    downloadCredentials() {
        this.form.markAllAsTouched();
        if (this.form.invalid) return;
        const { username, newPassword } = this.form.getRawValue();
        const cluster = this.data.cluster;
        this.credentialsDownloading = true;
        this.data.serverAddresses$.pipe(first()).subscribe((addresses) => {
            const version = parseVersionString(cluster.typeDBVersion.version);
            const properties = [
                `username = ${username}`,
                `password = ${newPassword}`,
                `addresses = [${addresses.join(", ")}]`,
                `connectionString = ${buildConnectionString(newPassword, addresses, version, cluster.id, username)}`
            ];
            const blob = new Blob([properties.join("\n")], { type: "text/plain" });
            const anchor = document.createElement("a");
            anchor.href = URL.createObjectURL(blob);
            anchor.download = clusterCredentialFilename(cluster);
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            this.credentialsDownloading = false;
            this.snackbar.success("Downloaded", { duration: 1250 });
            this.submit(true);
        });
    }

    submit(credentialsDownloaded: boolean) {
        this.form.markAllAsTouched();
        if (this.form.invalid) return;
        const { username, newPassword } = this.form.getRawValue();
        this.form.disable();
        this.isSubmitting$.next(true);
        this.clusterApi.resetUserPassword({ clusterUuid: this.data.cluster.uuid, username, newPassword }).subscribe({
            next: () => {
                if (!credentialsDownloaded) this.snackbar.success(`The password of user '${username}' has been reset.`);
                this.isSubmitting$.next(false);
                this.close("ok");
            },
            error: () => {
                this.isSubmitting$.next(false);
                this.form.enable();
            },
        });
    }

    reauthStageChanged(stage: ReauthStage) {
        if (stage === "reauthenticate") this.dialogTitle$.next("Reauthenticate to reset user password");
        else if (stage === "verify-identity") this.dialogTitle$.next("Verify your identity");
        else if (stage === "complete") {
            this.dialogTitle$.next("Reset user password");
            this.reauthRequired = false;
        }
    }

    close(result?: DialogResult) {
        this.dialogRef.close(result);
    }
}
